/**
 * 「データの取り出し」の画面で使う言葉（issue #114・決定22「状態を言葉で見せる」）。
 *
 * 値そのものの定義元はサーバ（`server/src/exports/`）。ここはその**言い換え**だけ。
 */
import type { DataExportItem } from '../api/types.js';
import { formatDateTime } from '../format/datetime.js';

/** 状態の短い名前（表の札に出す）。 */
export function exportStatusLabel(status: DataExportItem['status']): string {
  switch (status) {
    case 'queued':
      return '順番待ち';
    case 'running':
      return '作っています';
    case 'ready':
      return '受け取れます';
    case 'failed':
      return '作れませんでした';
    case 'expired':
      return '期限が過ぎたため消しました';
  }
}

/** 状態の札の色（受け取れる＝緑・作っている途中＝灰色・終わったもの＝薄い灰色）。 */
export function exportStatusTone(status: DataExportItem['status']): 'ok' | 'neutral' | 'muted' {
  if (status === 'ready') return 'ok';
  if (status === 'queued' || status === 'running') return 'neutral';
  return 'muted';
}

/** 状態の説明の1文（札の下に出す）。 */
export function exportStatusDetail(item: DataExportItem): string {
  switch (item.status) {
    case 'queued':
      return 'まもなく作り始めます（長くて1分ほど）。';
    case 'running': {
      const messages = item.summary.messages ?? 0;
      const files = item.summary.files ?? 0;
      return item.include_files
        ? `いまメッセージ${messages.toLocaleString('ja-JP')}件・写真とファイル${files.toLocaleString('ja-JP')}件まで入れました。`
        : `いまメッセージ${messages.toLocaleString('ja-JP')}件まで入れました。`;
    }
    case 'ready':
      return `${formatDateTime(item.expires_at)}まで受け取れます。`;
    case 'failed':
      return item.failure_reason ?? '時間をおいて、もう一度「取り出す」を押してください。';
    case 'expired':
      return 'もう一度取り出すときは、上の「取り出す」を押してください。';
  }
}

/** いま作っている途中か（順番待ちを含む）。 */
export function isExportActive(item: DataExportItem): boolean {
  return item.status === 'queued' || item.status === 'running';
}

/** 何を入れたかの1行（形式と、写真・ファイルを入れたか）。 */
export function exportContentLabel(item: DataExportItem): string {
  return `${item.format_label}・${item.include_files ? '写真とファイルつき' : '写真とファイルなし'}`;
}

/** ファイルの大きさ（「12.3 MB」の形）。 */
export function formatBytes(bytes: number | null): string {
  if (bytes === null) return '―';
  const units = ['バイト', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  if (unit === 0) return `${bytes.toLocaleString('ja-JP')} バイト`;
  return `${value >= 100 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}
